type ColorStops = 50 | 100 | 200 | 300 | 400 | 500 | 600 | 700 | 800 | 900 | 950;
type PrimaryColorStops = 25 | 50 | 100 | 200 | 300 | 400 | 500 | 600 | 700 | 800 | 900 | 950;

export type PaletteScale<T extends number | string> = Record<T, string>;

export type PrimaryPalette = PaletteScale<PrimaryColorStops>;

export type SecondaryPalette = PaletteScale<ColorStops>;

export type NeutralPalette = PaletteScale<ColorStops | 'white' | 'black'>;

export type InfoPalette = PaletteScale<50 | 100 | 200 | 300 | 400 | 500>;

export type GreyPalette = PaletteScale<100 | 200 | 300 | 400 | 500>;

export type BerryPalette = PaletteScale<100 | 200 | 300 | 400>;

export type TealPalette = PaletteScale<100 | 200 | 300 | 400 | 500>;

export type TertiaryPalette = PaletteScale<50 | 600>;

export type OrangePalette = PaletteScale<100 | 200 | 300 | 400>;

export type PurplePalette = PaletteScale<100 | 200 | 300 | 400>;

export interface SemanticColors {
    text: {
        default: string;
        subtle: string;
        muted: string;
        mutedLight: string;
        error: string;
        warning: string;
        success: string;
        info: string;
        brand: string;
        inverse: string;
        headerTabs: string;
    };
    icon: {
        dark: string;
        default: string;
        subtle: string;
        brand: string;
        error: string;
        warning: string;
        success: string;
        info: string;
        inverse: string;
        secondary: string;
    };
    bg: {
        default: string;
        subtle: string;
        raised: string;
        inverse: string;
        error: string;
        success1: string;
        success2: string;
        warning1: string;
        warning2: string;
        info: string;
        disabled: string;
        brand: string;
        brandDark: string;
        brandLight1: string;
        brandLight2: string;
        secondary1: string;
        secondary2: string;
        short: string;
        medium: string;
        long: string;
    };
    bd: {
        default: string;
        subtle: string;
        strong: string;
        inverse: string;
        error: string;
        warning: string;
        success: string;
        brand: string;
        info: string;
        brandDark: string;
        brandLight2: string;
        table: string;
        secondary: string;
        short: string;
        medium: string;
        long: string;
    };
    int: {
        hover: string;
        hoverButton: string;
        focused: string;
        pressed: string;
        selected: string;
        disabled1: string;
        disabled2: string;
        disabled3: string;
        positive: string;
        negative: string;
        noChange: string;
        link: string;
        error: string;
        radioButton: string;
        checkBox: string;
    };
    dat: {
        leading: string;
        lagging: string;
        asst: {
            Cash: string;
            Equity: string;
        };
    };
    leg: {
        today: string;
        oneWeekAgo: string;
        oneMonthAgo: string;
        threeMonthsAgo: string;
        oneYearAgo: string;
    };
    assetAllocation: {
        allocation: string;
        alternative: string;
        cash: string;
        cashSecondary: string;
        commodities: string;
        equity: string;
        fixedIncome: string;
        fixedIncomeSecondary: string;
    };
}

export interface TenantPalette {
    primary: PrimaryPalette;
    secondary: SecondaryPalette;
    secondary2: SecondaryPalette;
    neutral: NeutralPalette;
    error: SecondaryPalette;
    warning: SecondaryPalette;
    success: SecondaryPalette;
    info: InfoPalette;
    grey: GreyPalette;
    berry: BerryPalette;
    teal: TealPalette;
    tertiary: TertiaryPalette;
    orange: OrangePalette;
    purple: PurplePalette;
    semantic: SemanticColors;
}

export const newPalettes: Record<'advice' | 'banortemex', TenantPalette> = {
    advice: {
        primary: {
            25: '#F0F6FF',
            50: '#F0F6FF',
            100: '#DDEAFF',
            200: '#ADC2FF',
            300: '#85A3FF',
            400: '#6188FF',
            500: '#3769FF', // primary color
            600: '#1446E1',
            700: '#002EB8',
            800: '#001E62',
            900: '#000F3D',
            950: '#00081F'
        },
        secondary: {
            50: '#F5F2FD',
            100: '#EEE9FC',
            200: '#D7C9F8',
            300: '#C0AAF3',
            400: '#9F7DED',
            500: '#6730E3',
            600: '#571EDC',
            700: '#4C1AC1',
            800: '#3E159D',
            900: '#2C0F70',
            950: '#1E0A4C'
        },
        secondary2: {
            50: '#FAF1FD',
            100: '#F7E8FC',
            200: '#EECDF9',
            300: '#E3ACF6',
            400: '#D683F1',
            500: '#C042EA',
            600: '#B623E7',
            700: '#A417D3',
            800: '#8112A5',
            900: '#5D0D77',
            950: '#400953'
        },
        tertiary: {
            50: '#DEFEDD',
            600: '#177246'
        },
        neutral: {
            white: '#FFFFFF',
            50: '#F4F5F5',
            100: '#ECEDEE',
            200: '#D4D5D8',
            300: '#BCBDC3',
            400: '#9EA1A8',
            500: '#737680',
            600: '#686B73',
            700: '#5C5E66',
            800: '#484A51',
            900: '#38393E',
            950: '#212121',
            black: '#000000'
        },
        error: {
            50: '#FFF5F8',
            100: '#FFE5ED',
            200: '#FFCCDA',
            300: '#FFA8C1',
            400: '#FF7AA0',
            500: '#FF0F52',
            600: '#EB0042',
            700: '#CC003A',
            800: '#AD0031',
            900: '#750021',
            950: '#570019'
        },
        warning: {
            50: '#FFEFEB',
            100: '#FFDFD6',
            200: '#FFBFAD',
            300: '#FF9F85',
            400: '#FF7F5C',
            500: '#FF6035',
            600: '#F53500',
            700: '#B82800',
            800: '#7A1B00',
            900: '#3D0D00',
            950: '#1F0700'
        },
        success: {
            50: '#E0FFF6',
            100: '#D0FFF2',
            200: '#C0FCEC',
            300: '#72DBD5',
            400: '#00D6C8',
            500: '#00BFB3',
            600: '#00ADA2',
            700: '#00998F',
            800: '#008077',
            900: '#005C56',
            950: '#00423E'
        },
        info: {
            50: '#DDEAFF',
            100: '#91B9FF',
            200: '#5291FF',
            300: '#3769FF',
            400: '#1446E1',
            500: '#0D37BA'
        },
        grey: {
            100: '#E6E6E6',
            200: '#BFBFBF',
            300: '#8C8C8C',
            400: '#595959',
            500: '#333333'
        },
        berry: {
            100: '#FFDDE6',
            200: '#F589A2',
            300: '#F04F74',
            400: '#DC0546'
        },
        teal: {
            100: '#D3FBF7',
            200: '#72DBD5',
            300: '#00A096',
            400: '#00847D',
            500: '#CCE6E5'
        },
        orange: {
            100: '#FFE0D2',
            200: '#FF9665',
            300: '#FF6035',
            400: '#BE3200'
        },
        purple: {
            100: '#E7DFFE',
            200: '#B39AF5',
            300: '#8760EF',
            400: '#4B00B6'
        },
        semantic: {
            text: {
                default: '#212121', // NC/950
                subtle: '#484A51', // NC/800
                muted: '#686B73', // NC/600
                mutedLight: '#9EA1A8', // NC/400
                error: '#AD0031', // sta/ER/800
                warning: '#B82800', // sta/WA/700
                success: '#008077', // sta/SU/800
                info: '#0D37BA', // sta/IN/500
                brand: '#001E62', // PC/700
                inverse: '#FFFFFF', // NC/white
                headerTabs: '#DDEAFF' //PC/100
            },
            icon: {
                dark: '#212121', // NC/950
                default: '#5C5E66', // NC/700
                subtle: '#737680', // NC/500
                brand: '#3769FF', // PC/500
                error: '#EB0042', // sta/ER/600
                warning: '#F53500', // sta/WA/600
                success: '#00ADA2', // sta/SU/600
                info: '#3769FF', // sta/IN/300
                inverse: '#FFFFFF', // NC/white
                secondary: '#C042EA' // SC2/500
            },
            bg: {
                default: '#FFFFFF', // NC/white
                subtle: '#F4F5F5', // NC/50
                raised: '#ECEDEE', // NC/100
                inverse: '#38393E', // NC/900
                error: '#FFE5ED', // sta/ER/100
                success1: '#E0FFF6', // sta/SU/50,
                success2: '#C0FCEC', // sta/SU/200
                warning1: '#FFEFEB', // sta/WA/50
                warning2: '#FFDFD6', // sta/WA/100
                info: '#DDEAFF', // sta/IN/50
                disabled: '#D4D5D8', // NC/200
                brand: '#3769FF', // PC/500
                brandDark: '#001E62', // PC/800
                brandLight1: '#F0F6FF', // PC/50
                brandLight2: '#DDEAFF', // PC/100
                secondary1: '#EEE9FC', // SC1/100
                secondary2: '#F7E8FC', // SC2/100
                short: '#D3C5E2', // tenure/short-light
                medium: '#ABC8CC', // tenure/medium-light
                long: '#B2C0EA' // tenure/long-light
            },
            bd: {
                default: '#D4D5D8', // NC/200
                subtle: '#BCBDC3', // NC/300
                strong: '#9EA1A8', // NC/400
                inverse: '#FFFFFF', // NC/white
                error: '#EB0042', // sta/ER/600
                warning: '#F53500', // sta/WA/600
                success: '#00998F', // sta/SU/700
                brand: '#3769FF', // PC/500
                info: '#1446E1', // sta/IN/400
                brandDark: '#001E62', // PC/800
                brandLight2: '#DDEAFF', // PC/100
                table: '#ECEDEE', // NC/100
                secondary: '#C042EA', // SC2/500
                short: '#2D1249', // tenure/short-dark
                medium: '#005135', // tenure/medium-dark
                long: '#00175C' // tenure/long-dark
            },
            int: {
                hover: '#DDEAFF', // PC/100
                hoverButton: '#85A3FF', // PC/300
                focused: '#3769FF', // sta/IN/300
                pressed: '#001E62', // PC/800
                selected: '#3769FF', // PC/500
                disabled1: '#F4F5F5', // NC/50
                disabled2: '#9EA1A8', // NC/400
                disabled3: '#737680', // NC/500
                positive: '#00998F', // sta/SU/700
                negative: '#CC003A', // sta/ER/700
                noChange: '#000000', // NC/black
                link: '#3769FF', // sta/IN/300
                error: '#EB0042', // sta/ER/600
                radioButton: '#3769FF', // sta/IN/300
                checkBox: '#3769FF' // sta/IN/300
            },
            dat: {
                leading: '#72DBD5', // sta/SU/300
                lagging: '#FF9F85', // sta/WA/300
                asst: {
                    Cash: '#005C56', // sta/SU/900
                    Equity: '#00ADA2' // dat/asst/equity
                }
            },
            leg: {
                today: '#3769FF', // PC/500
                oneWeekAgo: '#00BFB3', // sta/SU/500
                oneMonthAgo: '#C042EA', // SC2/500
                threeMonthsAgo: '#FF6035', // sta/WA/500
                oneYearAgo: '#6730E3' // SC1/500
            },
            assetAllocation: {
                allocation: '#FF6035', // warning[500]
                alternative: '#5291FF', // info[200]
                cash: '#00847D', // success[800] approx
                cashSecondary: '#CCE3E5',
                commodities: '#C042EA', // secondary2[500]
                equity: '#00BFB3', // success[500]
                fixedIncome: '#6730E3', // secondary[500]
                fixedIncomeSecondary: '#E7DFFE'
            }
        }
    },
    banortemex: {
        primary: {
            25: '#EDEAED',
            50: '#DBD6DB',
            100: '#B7ACB7',
            200: '#928392',
            300: '#6E596E',
            400: '#5C455C',
            500: '#4A304A', // primary color
            600: '#402940',
            700: '#372337',
            800: '#2D1C2D',
            900: '#241624',
            950: '#1A0F1A'
        },
        secondary: {
            50: '#F5F2FD',
            100: '#EEE9FC',
            200: '#D7C9F8',
            300: '#C0AAF3',
            400: '#9F7DED',
            500: '#6730E3',
            600: '#571EDC',
            700: '#4C1AC1',
            800: '#3E159D',
            900: '#2C0F70',
            950: '#1E0A4C'
        },
        secondary2: {
            50: '#FAF1FD',
            100: '#F7E8FC',
            200: '#EECDF9',
            300: '#E3ACF6',
            400: '#D683F1',
            500: '#C042EA',
            600: '#B623E7',
            700: '#A417D3',
            800: '#8112A5',
            900: '#5D0D77',
            950: '#400953'
        },
        tertiary: {
            50: '#DEFEDD',
            600: '#177246'
        },
        neutral: {
            white: '#FFFFFF',
            50: '#F4F5F5',
            100: '#ECEDEE',
            200: '#D4D5D8',
            300: '#BCBDC3',
            400: '#9EA1A8',
            500: '#737680',
            600: '#686B73',
            700: '#5C5E66',
            800: '#484A51',
            900: '#38393E',
            950: '#212121',
            black: '#000000'
        },
        error: {
            50: '#FFF5F8',
            100: '#FFE5ED',
            200: '#FFCCDA',
            300: '#FFA8C1',
            400: '#FF7AA0',
            500: '#FF0F52',
            600: '#EB0042',
            700: '#CC003A',
            800: '#AD0031',
            900: '#750021',
            950: '#570019'
        },
        warning: {
            50: '#FFEFEB',
            100: '#FFDFD6',
            200: '#FFBFAD',
            300: '#FF9F85',
            400: '#FF7F5C',
            500: '#FF6035',
            600: '#F53500',
            700: '#B82800',
            800: '#7A1B00',
            900: '#3D0D00',
            950: '#1F0700'
        },
        success: {
            50: '#E0FFF6',
            100: '#D0FFF2',
            200: '#C0FCEC',
            300: '#72DBD5',
            400: '#00D6C8',
            500: '#00BFB3',
            600: '#00ADA2',
            700: '#00998F',
            800: '#008077',
            900: '#005C56',
            950: '#00423E'
        },
        info: {
            50: '#DDEAFF',
            100: '#91B9FF',
            200: '#5291FF',
            300: '#3769FF',
            400: '#1446E1',
            500: '#0D37BA'
        },
        grey: {
            100: '#E6E6E6',
            200: '#BFBFBF',
            300: '#8C8C8C',
            400: '#595959',
            500: '#333333'
        },
        berry: {
            100: '#FFDDE6',
            200: '#F589A2',
            300: '#F04F74',
            400: '#DC0546'
        },
        teal: {
            100: '#D3FBF7',
            200: '#72DBD5',
            300: '#00A096',
            400: '#00847D',
            500: '#CCE6E5'
        },
        orange: {
            100: '#FFE0D2',
            200: '#FF9665',
            300: '#FF6035',
            400: '#BE3200'
        },
        purple: {
            100: '#E7DFFE',
            200: '#B39AF5',
            300: '#8760EF',
            400: '#4B00B6'
        },
        semantic: {
            text: {
                default: '#212121', // NC/950
                subtle: '#484A51', // NC/800
                muted: '#686B73', // NC/600
                mutedLight: '#9EA1A8', // NC/400
                error: '#AD0031', //sta/ER/800
                warning: '#B82800', //sta/WA/700
                success: '#008077', //sta/SU/800
                info: '#0D37BA', //sta/IN/500
                brand: '#372337', // PC/700 (banortemex primary)
                inverse: '#FFFFFF', // NC/white
                headerTabs: '#B7ACB7' //PC/100
            },
            icon: {
                dark: '#212121', // NC/950
                default: '#5C5E66', // NC/700
                subtle: '#737680', // NC/500
                brand: '#4A304A', // PC/500 (banortemex primary)
                error: '#EB0042', //sta/ER/600
                warning: '#F53500', //sta/WA/600
                success: '#00ADA2', //sta/SU/600
                info: '#3769FF', //sta/IN/300
                inverse: '#FFFFFF', // NC/white
                secondary: '#C042EA' // SC2/500
            },
            bg: {
                default: '#FFFFFF', // NC/white
                subtle: '#F4F5F5', // NC/50
                raised: '#ECEDEE', // NC/100
                inverse: '#38393E', // NC/900
                error: '#FFE5ED', // sta/ER/100
                success1: '#E0FFF6', // sta/SU/50
                success2: '#C0FCEC', // sta/SU/200
                warning1: '#FFEFEB', // sta/WA/50
                warning2: '#FFDFD6', // sta/WA/100
                info: '#DDEAFF', // sta/IN/50
                disabled: '#D4D5D8', // NC/200
                brand: '#4A304A', // PC/500 (banortemex primary)
                brandDark: '#2D1C2D', // PC/800
                brandLight1: '#DBD6DB', // PC/50
                brandLight2: '#B7ACB7', // PC/100
                secondary1: '#EEE9FC', // SC1/100
                secondary2: '#F7E8FC', // SC2/100
                short: '#D3C5E2', // tenure/short-light
                medium: '#ABC8CC', // tenure/medium-light
                long: '#B2C0EA' // tenure/long-light
            },
            bd: {
                default: '#D4D5D8', // NC/200
                subtle: '#BCBDC3', // NC/300
                strong: '#9EA1A8', // NC/400
                inverse: '#FFFFFF', // NC/white
                error: '#EB0042', //sta/ER/600
                warning: '#F53500', //sta/WA/600
                success: '#00998F', //sta/SU/700
                brand: '#4A304A', // PC/500 (banortemex primary)
                info: '#1446E1', //sta/IN/400
                brandDark: '#2D1C2D', // PC/800
                brandLight2: '#B7ACB7', // PC/100
                table: '#ECEDEE', // NC/100
                secondary: '#C042EA', // SC2/500
                short: '#2D1249', // tenure/short-dark
                medium: '#005135', // tenure/medium-dark
                long: '#00175C' // tenure/long-dark
            },
            int: {
                hover: '#B7ACB7', // PC/100
                hoverButton: '#6E596E', // PC/300
                focused: '#3769FF', // sta/IN/300
                pressed: '#2D1C2D', // PC/800
                selected: '#4A304A', // PC/500
                disabled1: '#F4F5F5', // NC/50
                disabled2: '#9EA1A8', // NC/400
                disabled3: '#737680', // NC/500
                positive: '#00998F', // sta/SU/700
                negative: '#CC003A', // sta/ER/700
                noChange: '#000000', // NC/black
                link: '#3769FF', // sta/IN/300
                error: '#EB0042', // sta/ER/600
                radioButton: '#3769FF', // sta/IN/300
                checkBox: '#3769FF' // sta/IN/300
            },
            dat: {
                leading: '#72DBD5', // sta/SU/300
                lagging: '#FF9F85', //sta/WA/300
                asst: {
                    Cash: '#005C56', // sta/SU/900
                    Equity: '#00ADA2' // dat/asst/equity
                }
            },
            leg: {
                today: '#4A304A', // PC/500
                oneWeekAgo: '#00BFB3', // sta/SU/500
                oneMonthAgo: '#C042EA', // SC2/500
                threeMonthsAgo: '#FF6035', // sta/WA/500
                oneYearAgo: '#6730E3' // SC1/500
            },
            assetAllocation: {
                allocation: '#FF6035', // warning[500]
                alternative: '#5291FF', // info[200]
                cash: '#00847D', // success[800] approx
                commodities: '#C042EA', // secondary2[500]
                equity: '#00BFB3', // success[500]
                fixedIncome: '#6730E3', // secondary[500]
                fixedIncomeSecondary: '#E7DFFE', // secondary[200] approx
                cashSecondary: '#CCE3E5' // success[200] approx
            }
        }
    }
};

export type TenantType = keyof typeof newPalettes;

export const getNewPalette = (tenant: TenantType, paletteSource: typeof newPalettes = newPalettes): TenantPalette => {
    // change to 'advice' as default, if tenant not found
    const palette = paletteSource[tenant in paletteSource ? tenant : 'advice'];
    return {
        ...palette,
        primary: { ...palette.primary, main: palette.primary[500] },
        secondary: { ...palette.secondary, main: palette.secondary[500] },
        secondary2: { ...palette.secondary2, main: palette.secondary2[500] },
        error: { ...palette.error, main: palette.error[500] },
        warning: { ...palette.warning, main: palette.warning[500] },
        success: { ...palette.success, main: palette.success[500] },
        info: { ...palette.info, main: palette.info[400] }
    } as any;
};

export const formatpalette = (palette: TenantPalette) => {
    return {
        ...palette,
        primary: { ...palette.primary, main: palette.primary[500] },
        secondary: { ...palette.secondary, main: palette.secondary[500] },
        secondary2: { ...palette.secondary2, main: palette.secondary2[500] },
        error: { ...palette.error, main: palette.error[500] },
        warning: { ...palette.warning, main: palette.warning[500] },
        success: { ...palette.success, main: palette.success[500] },
        info: { ...palette.info, main: palette.info[400] }
    } as any;
};

export const mergeWithDefaultPalette = (uploadedPalette: Partial<TenantPalette>, tenantId: string): TenantPalette => {
    const defaultPalette = tenantId in newPalettes ? newPalettes[tenantId as TenantType] : newPalettes['advice'];
    const merged: TenantPalette = JSON.parse(JSON.stringify(defaultPalette)); // deep clone default
    const deepMerge = (target: any, source: any) => {
        for (const key of Object.keys(source)) {
            if (source[key] !== null && typeof source[key] === 'object' && !Array.isArray(source[key])) {
                if (!target[key] || typeof target[key] !== 'object') {
                    target[key] = {};
                }
                deepMerge(target[key], source[key]);
            } else if (source[key] !== undefined) {
                target[key] = source[key];
            }
        }
        return target;
    };
    return deepMerge(merged, uploadedPalette);
};
